import { type HTMLProps, useMemo } from 'react';

import clsx from 'clsx';

import { Cancel, Error } from '../Icons';

import styles from './Input.module.scss';

type ContainerProps = HTMLProps<HTMLDivElement> & {
  /**
   * The translated string to use for the aria-label of the icon that clears the input.
   */
  ariaLabelClearInput: string;

  /**
   * The translated string or <T> element to use when there is an error with value entered into the input.
   */
  errorText?: React.ReactNode;

  /**
   * The state to represent if the input should be disabled.
   */
  isDisabled?: boolean;

  /**
   * The translated string or <T> element to use as the input's label.
   */
  labelText: React.ReactNode;

  /**
   * Hide the label visually, but keep it accessible for screen readers.
   */
  hideLabel?: boolean;

  /**
   * The function used to clear the value entered into the input.
   */
  onClear: () => void;

  /**
   * The translated string or <T> element to use as the input's sublabel (e.g. info about password restrictions).
   */
  supportingText?: React.ReactNode;

  /**
   * The translated string to use as the input's placeholder.
   */
  children: React.ReactNode;

  /**
   * The custom icon to render inside the input.
   */
  customIcon?: React.ReactNode;
};

export const Container = ({
  ariaLabelClearInput,
  className,
  errorText,
  isDisabled,
  labelText,
  hideLabel = false,
  name,
  onClear,
  supportingText,
  children,
  customIcon,
  ...containerProps
}: ContainerProps) => {
  const InuptRightSection = useMemo(() => {
    if (customIcon) {
      return <div className={styles['custom-icon']}>{customIcon}</div>;
    }

    if (errorText) {
      return <Error size={24} className={styles.icon} />;
    }

    return (
      <button type="button" className={clsx(styles.clear, styles.icon)} onClick={onClear} tabIndex={-1}>
        <Cancel aria-label={ariaLabelClearInput} size={24} />
      </button>
    );
  }, [customIcon, errorText, onClear, ariaLabelClearInput]);

  return (
    <div
      className={clsx(styles.container, className, {
        [styles.error]: Boolean(errorText),
        [styles.disabled]: isDisabled,
      })}
      {...containerProps}
    >
      {labelText && (
        <label className={clsx(styles.label, { [styles.hidden]: hideLabel })} htmlFor={name}>
          {labelText}
        </label>
      )}

      {supportingText ? <div className={styles['sub-text']}>{supportingText}</div> : null}

      <div className={styles.input}>
        <div
          className={clsx(styles['input-field'], { [styles.error]: Boolean(errorText), [styles.disabled]: isDisabled })}
        >
          {children}
          {InuptRightSection}
        </div>
        {errorText ? <div className={styles['sub-text']}>{errorText}</div> : null}
      </div>
    </div>
  );
};
